import React from 'react';
import {
	Box,
	Heading,
	Text,
	VStack,
	Flex,
	HStack,
	Switch,
	Input,
	Button,
	Badge,
	Divider,
	Avatar,
	IconButton,
	Icon,
	Spinner,
} from '@chakra-ui/react';
import { FiX, FiUserPlus, FiAlertTriangle, FiRefreshCw } from 'react-icons/fi';
import useAppToast from 'components/toast';
import Card from 'components/card/Card';
import { usePermissions } from 'hooks/usePermissions';
import UserPicker, { userLabel } from 'components/userPicker/UserPicker';
import TestSend from './TestSend';
import {
	usePatchChannelMutation,
	useListAssignmentsQuery,
	useAssignUserMutation,
	useRevokeUserMutation,
	useListAssignableUsersQuery,
	useSyncChannelsMutation,
} from 'api/metaWhatsappApi';

/**
 * One connected number: its name, whether it sends, whether it is the default,
 * and who may use it.
 *
 * ★ ONE CARD PER RESOURCE, EACH WITH ITS OWN SAVE.
 * A single page-level Save can half-succeed — one channel renamed, the next
 * refused — and leave the screen showing state that was never stored. Per-card
 * saves make each outcome unambiguous.
 *
 * ★ EACH FIELD HAS ITS OWN PERMISSION SERVER-SIDE.
 * Rename, enable and set-default are three different decisions. The UI does not
 * try to predict which the caller holds — it sends the change and reports what
 * comes back, because the client permission map is stale until reload.
 *
 * ★ ASSIGNMENT IS THE AXIS THAT MAKES A NUMBER VISIBLE. Nobody sees a
 * conversation on a number they are not assigned to, whatever their role — so
 * the list of people is on the number itself, where an administrator looks.
 */

/**
 * ★ WHY A NUMBER CANNOT SEND, IN WORDS, PER BLOCKER.
 * The connection layer keeps these four as independent flags and refuses to
 * fold them into one status, because clearing a Meta restriction does not
 * clear a token that also needs re-authenticating. Collapsing them here would
 * throw away exactly the distinction that tells an administrator what to do.
 *
 * Until now the CRM could not see them at all: the registry projection carries
 * routing facts only, so a number that had quietly stopped working showed as
 * healthy and the failure surfaced as a refused send with no explanation.
 */
const BLOCKER_TEXT = {
	metaDisconnected: 'Disconnected at Meta',
	tokenNeedsReauth: 'Access token needs reconnecting',
	accountRestricted: 'Restricted by Meta',
	healthDegraded: 'Limited by Meta for now',
};

// The label and the debounce live with the picker now — one definition of
// "what do we call this person" across every screen that offers a list of them.
const displayName = userLabel;

function AssignedUsers({ phoneNumberId }) {
	const notify = useAppToast();
	const { data: assignments = [], isLoading } = useListAssignmentsQuery(phoneNumberId);
	const [assignUser, { isLoading: assigning }] = useAssignUserMutation();
	const [revokeUser] = useRevokeUserMutation();

	// ★ THE PICKER OWNS THE QUERY STATE; THIS OWNS THE ENDPOINT.
	// Passing a search function rather than a URL is what lets the same control
	// serve the assignment screen and the setup wizard without either of them
	// learning the other's permissions.
	const useSearchPeople = (q, page, isOpen) => {
		const { data, isFetching } = useListAssignableUsersQuery(
			{ phoneNumberId, q, page },
			{ skip: !isOpen },
		);
		return { users: data?.users ?? [], pagination: data?.pagination ?? null, isFetching };
	};

	const add = async (user) => {
		try {
			await assignUser({ phoneNumberId, userId: user._id }).unwrap();
			notify.success('Assigned', `${displayName(user)} can now use this number.`);
		} catch (err) {
			notify.error('Not assigned', err?.data?.reason ?? 'That assignment could not be saved.');
		}
	};

	const remove = async (assignment) => {
		try {
			await revokeUser({ phoneNumberId, userId: assignment.user?._id ?? assignment.user, reason: 'manual' }).unwrap();
			notify.success('Removed', `${displayName(assignment.user)} no longer sees this number.`);
		} catch (err) {
			notify.error('Not removed', 'That assignment could not be removed.');
		}
	};

	return (
		<Box>
			<HStack justify="space-between" mb="2">
				<Box>
					<Text fontSize="sm" color="text.primary">
						People on this number
					</Text>
					<Text fontSize="xs" color="text.muted">
						Only assigned people see its conversations. Assigning takes effect immediately.
					</Text>
				</Box>
			</HStack>

			{isLoading ? (
				<Spinner size="xs" />
			) : (
				<VStack align="stretch" spacing="1.5" mb="3">
					{assignments.length === 0 && (
						<Text fontSize="xs" color="text.muted" fontStyle="italic">
							Nobody yet — this number's conversations are invisible until someone is assigned.
						</Text>
					)}
					{assignments.map((a) => (
						<HStack
							key={a._id}
							spacing="2"
							px="2"
							py="1.5"
							borderRadius="md"
							bg="bg.subtle"
							justify="space-between"
						>
							<HStack spacing="2" minW="0">
								<Avatar size="xs" name={displayName(a.user)} />
								<Box minW="0">
									<HStack spacing="1.5">
										<Text fontSize="sm" color="text.primary" noOfLines={1}>
											{displayName(a.user)}
										</Text>
										{/* ★ ASSIGNED IS NOT THE SAME AS ABLE TO SEE IT.
										    Their role has to grant the WhatsApp inbox as well,
										    and a role can change long after this assignment
										    was made — so it is checked here, not assumed. */}
										{a.user?.canUseInbox === false && (
											<Badge colorScheme="yellow" variant="subtle" fontSize="0.55rem">
												No inbox access
											</Badge>
										)}
									</HStack>
									<Text
										fontSize="0.65rem"
										color={a.user?.canUseInbox === false ? 'warning' : 'text.muted'}
										noOfLines={1}
									>
										{a.user?.canUseInbox === false
											? "Their role grants no WhatsApp access — assigning alone won't show them anything"
											: a.user?.email}
									</Text>
								</Box>
							</HStack>
							<IconButton
								aria-label={`Remove ${displayName(a.user)}`}
								size="xs"
								variant="ghost"
								icon={<Icon as={FiX} />}
								onClick={() => remove(a)}
							/>
						</HStack>
					))}
				</VStack>
			)}

			{/* ★ THE WARNING IS SAID BEFORE THE CHOICE, not discovered after it.
			    A candidate whose role grants no WhatsApp module will see nothing
			    once assigned. They are offered anyway: the fix is to grant the
			    role, and hiding them would hide that too. */}
			<UserPicker
				useSearch={useSearchPeople}
				onSelect={add}
				isBusy={assigning}
				placeholder="Add a person by name or email…"
				exhaustedText="Everyone is already assigned."
				renderMeta={(u) =>
					u.canUseInbox === false ? (
						<Badge colorScheme="yellow" variant="subtle" fontSize="0.55rem">
							No inbox access
						</Badge>
					) : null
				}
				renderHint={(u) =>
					u.canUseInbox === false ? 'Needs the WhatsApp module on their role' : u.email
				}
			/>
		</Box>
	);
}

/**
 * A number the platform routes to this workspace with no local row.
 *
 * ★ IT IS NOT A DISPLAY PROBLEM, IT IS A DEAD NUMBER.
 * Without a row, inbound is refused as TENANT_MISMATCH and dead-lettered by the
 * connection layer — the customer's message is destroyed, not delayed — and
 * there is no card to assign anyone on. So it is shown loudly, with the one
 * action that fixes it, rather than omitted because it is "not configured".
 */
function UnconfiguredChannel({ channel }) {
	const notify = useAppToast();
	const { hasPermission } = usePermissions();
	const canConnect = hasPermission('whatsapp', 'integration.connect');
	const [syncChannels, { isLoading }] = useSyncChannelsMutation();

	const repair = async () => {
		try {
			const result = await syncChannels().unwrap();
			notify.success(
				'Numbers synced',
				result.created > 0
					? `${result.created} number${result.created === 1 ? '' : 's'} added.`
					: 'Everything the connection service reports is already here.',
			);
		} catch (err) {
			notify.error(
				'Not synced',
				err?.data?.error === 'CONNECTION_LAYER_TOO_OLD'
					? 'The connection service is an older build that cannot report its numbers. It needs deploying first.'
					: 'The numbers could not be read from the connection service.',
			);
		}
	};

	return (
		<Card bg="bg.surface" borderWidth="1px" borderColor="warning">
			<Flex justify="space-between" align="start" flexWrap="wrap" gap="4">
				<HStack spacing="3" align="start" minW="0">
					<Icon as={FiAlertTriangle} color="warning" boxSize="5" mt="0.5" />
					<Box minW="0">
						<Heading size="sm" color="text.primary">
							{channel.phoneNumberId}
						</Heading>
						<Text fontSize="xs" color="warning" mt="0.5">
							Known to the platform, not set up here — its messages cannot arrive and nobody can be
							assigned to it.
						</Text>
					</Box>
				</HStack>
				{canConnect && (
					<Button
						size="sm"
						colorScheme="yellow"
						leftIcon={<Icon as={FiRefreshCw} />}
						onClick={repair}
						isLoading={isLoading}
					>
						Set it up
					</Button>
				)}
			</Flex>
		</Card>
	);
}

export default function ChannelCard({ channel, onReconnect }) {
	if (channel.needsConfiguration) return <UnconfiguredChannel channel={channel} />;
	return <ConfiguredChannel channel={channel} onReconnect={onReconnect} />;
}

function ConfiguredChannel({ channel, onReconnect }) {
	const [label, setLabel] = React.useState(channel.label ?? '');
	const [patchChannel, { isLoading }] = usePatchChannelMutation();
	const notify = useAppToast();
	const { hasPermission } = usePermissions();
	const canAssign = hasPermission('whatsapp', 'channel.assign');

	// A refetch after someone else's edit should not clobber what is being typed.
	React.useEffect(() => {
		setLabel(channel.label ?? '');
	}, [channel.label]);

	const dirty = label !== (channel.label ?? '');
	// With no name of its own the heading IS the number, so the line under it
	// must not repeat it -- that read as a rendering fault rather than a card.
	const named = Boolean(channel.label);

	// The last step of setting a number up, and the easiest to forget: without
	// it the number works perfectly and nobody can see a word of it.
	const noneAssigned = (channel.assignedUsers ?? 0) === 0;
	// And the step after that: an assignment only counts if the person's ROLE
	// also grants the WhatsApp inbox. Assigning everyone and granting nobody
	// looks, from the count alone, exactly like a finished setup.
	const blocked = channel.assignedWithoutAccess ?? 0;
	const noneCanSee = !noneAssigned && blocked === (channel.assignedUsers ?? 0);

	const save = async (patch, description) => {
		try {
			await patchChannel({ phoneNumberId: channel.phoneNumberId, ...patch }).unwrap();
			notify.success('Saved', description);
		} catch (err) {
			const payload = err?.data ?? {};
			notify.error(
				'Not saved',
				payload.field ? `You do not have permission to change ${payload.field}.` : 'That change could not be saved.',
			);
		}
	};

	return (
		<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
			<Box mb="4">
				<HStack justify="space-between" align="start">
					<Box minW="0">
						<Heading size="sm" color="text.primary">
							{channel.label || channel.displayPhoneNumber || channel.phoneNumberId}
						</Heading>
						<Text fontSize="xs" color={noneAssigned || blocked > 0 ? 'warning' : 'text.muted'} mt="0.5">
							{named ? `${channel.displayPhoneNumber} · ` : ''}
							{noneAssigned ? 'nobody assigned' : `${channel.assignedUsers} assigned`}
							{blocked > 0 && ` · ${blocked} without inbox access`}
						</Text>
					</Box>
					<HStack spacing="2">
						{channel.isDefault && (
							<Badge colorScheme="orange" variant="subtle">
								Default
							</Badge>
						)}
						{/* A number Meta no longer lists. Sends are denied and history
						    stays readable — it is never removed automatically, because
						    one failed registry fetch must not delete configuration. */}
						{/* ★ ONE BADGE PER BLOCKER, not one badge for "unhealthy". */}
					{(channel.blockers ?? []).map((name) => (
						<Badge key={name} colorScheme="red" variant="subtle">
							{BLOCKER_TEXT[name] ?? name}
						</Badge>
					))}
					{/* ★ THE FIX, WHERE THE PROBLEM IS SHOWN. The badge above named the
					    fault; the only way to act on it was the account card further up,
					    and "does an admin have to acknowledge this?" was asked in so many
					    words. Same handler as the account card: it opens the connect
					    form with this number's ids already filled in. */}
					{(channel.blockers ?? []).includes('tokenNeedsReauth') && onReconnect && (
						<Button
							size="xs"
							variant="outline"
							colorScheme="red"
							leftIcon={<Icon as={FiRefreshCw} />}
							onClick={onReconnect}
						>
							Reconnect
						</Button>
					)}
					{channel.orphaned && (
							<Badge colorScheme="red" variant="subtle">
								Not at Meta
							</Badge>
						)}
						{!channel.orphaned && !channel.isRoutable && (
							<Badge colorScheme="yellow" variant="subtle">
								Not routable
							</Badge>
						)}
						{/* ★ ROUTABLE IS NOT THE SAME AS USABLE.
						    Meta will deliver to this number, but only assigned people see
						    its conversations — so with nobody on it every message arrives
						    and is seen by no one. Calling that "Live" is the one badge
						    that could let a workspace believe it had finished. */}
						{!channel.orphaned && channel.isRoutable && noneAssigned && (
							<Badge colorScheme="yellow" variant="subtle">
								Nobody assigned
							</Badge>
						)}
						{/* Assigned, but not one of them can open the inbox — the same
						    outcome as nobody assigned, reached a different way. */}
						{!channel.orphaned && channel.isRoutable && noneCanSee && (
							<Badge colorScheme="yellow" variant="subtle">
								Nobody can see it
							</Badge>
						)}
						{!channel.orphaned && channel.isRoutable && !noneAssigned && !noneCanSee && (
							<Badge colorScheme="green" variant="subtle">
								Live
							</Badge>
						)}
					</HStack>
				</HStack>
			</Box>

			<Box>
				<VStack spacing="4" align="stretch">
					<Box>
						<Text fontSize="xs" color="text.muted" mb="1">
							Display name
						</Text>
						<HStack spacing="2">
							<Input
								size="sm"
								value={label}
								onChange={(e) => setLabel(e.target.value)}
								placeholder="e.g. Dubai Sales"
								bg="bg.input"
								maxLength={60}
							/>
							<Button size="sm" onClick={() => save({ label }, 'Name saved')} isDisabled={!dirty} isLoading={isLoading}>
								Save
							</Button>
						</HStack>
					</Box>

					<Divider borderColor="border.subtle" />

					{/* ★ ABOVE THE SWITCHES ON PURPOSE. Assignment is the step an
					    administrator arrives looking for and the one that decides
					    whether anything is visible at all; the two toggles are
					    adjustments to a number that already works. */}
					{canAssign && <AssignedUsers phoneNumberId={channel.phoneNumberId} />}

					{canAssign && <Divider borderColor="border.subtle" />}

					{/* Once someone is on it, the only question left is whether it
					    really sends — so the proof sits directly under the people. */}
					{canAssign && <TestSend channel={channel} />}

					{canAssign && <Divider borderColor="border.subtle" />}

					<HStack justify="space-between">
						<Box>
							<Text fontSize="sm" color="text.primary">
								Enabled
							</Text>
							{/* Disabling stops SENDING only. Inbound keeps arriving, because
							    discarding a customer's message is not recoverable. */}
							<Text fontSize="xs" color="text.muted">
								Turning this off stops sending. Incoming messages still arrive.
							</Text>
						</Box>
						<Switch
							isChecked={channel.isEnabled}
							onChange={(e) =>
								save({ isEnabled: e.target.checked }, e.target.checked ? 'Number enabled' : 'Number disabled')
							}
						/>
					</HStack>

					<HStack justify="space-between">
						<Box>
							<Text fontSize="sm" color="text.primary">
								Default number
							</Text>
							<Text fontSize="xs" color="text.muted">
								Used first when starting a new conversation.
							</Text>
						</Box>
						<Switch
							isChecked={channel.isDefault}
							isDisabled={channel.isDefault}
							onChange={() => save({ isDefault: true }, 'Default number set')}
						/>
					</HStack>

				</VStack>
			</Box>
		</Card>
	);
}
